import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import claims from "../../../gql/claims";
import { useTranslation } from "react-i18next";
import AppButton from "../../../shared/AppButton";
import CustomFilterInput from "../../../shared/CustomFilterInput";

const ManualCancellationSchema = Yup.object().shape({
  request_value: Yup.string().required("Request Value is required"),
  request_name: Yup.string().required("Request Name is required"),
  payer_id: Yup.string().required("Payer is required"),
});

export default function ManualCancellation() {
  const { t } = useTranslation();
  const [payers, setPayers] = useState([]);
  const navigate = useNavigate();

  const [submitEntry, { loading: submitLoading }] = useMutation(
    claims.MANUAL_CANCELLATION,
    {
      update(proxy, result) {
        onSuccessfulRequestResponse(result.data);
      },
      onError(error) {
        onResponseError(error.graphQLErrors[0]);
      },
    },
  );

  const onSuccessfulRequestResponse = (data) => {
    if (data?.cancelRequest2) {
      toast.success(data?.cancelRequest2?.message);
    } else {
      toast.error(t("An error occurred while cancelling the claims."));
    }
  };

  const onResponseError = (error) => {
    toast.error(`${error?.message}`);
  };

  const handleManualCancellation = async (values, { resetForm }) => {
    try {
      await submitEntry({
        variables: {
          input: {
            request_value: values.request_value,
            request_name: values.request_name,
            payer_id: values.payer_id,
          },
        },
      });
      resetForm();
    } catch (error) {
      console.error(t("Error submitting manual cancellation:"), error);
    }
  };

  const systemTypes = [
    { value: "claims", name: t("Claims") },
    { value: "preAuth", name: t("PreAuth") },
  ];

  useEffect(() => {
    const payersData = JSON.parse(localStorage.getItem("payersData"));
    if (payersData && payersData.length > 0) {
      setPayers(payersData);
    } else {
      navigate("/dashboard");
    }
  }, []);

  return (
    <Formik
      initialValues={{ request_value: "", request_name: "", payer_id: "" }}
      validationSchema={ManualCancellationSchema}
      onSubmit={handleManualCancellation}
    >
      {({ errors, touched, values, handleChange, handleBlur }) => (
        <Form>
          <div className="w-1/2 ml-auto bg-white p-2 rounded-lg border border-slate-200">
            <div className="details-fields grid grid-cols-1 md:grid-cols-2 gap-x-1.5 gap-y-1.5 mb-1.5 pb-1.5 border-b">
              <CustomFilterInput
                label={t("Request Value")}
                name="request_value"
                placeholder={t("Enter Request Value")}
                value={values.request_value}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.request_value}
                touched={touched.request_value}
              />

              <CustomFilterInput
                label={t("Request Name")}
                name="request_name"
                isSelect={true}
                placeholder={t("Select Request Name")}
                options={systemTypes}
                value={values.request_name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.request_name}
                touched={touched.request_name}
              />

              <div className="md:col-span-2">
                <CustomFilterInput
                  label={t("Payer")}
                  name="payer_id"
                  isSelect={true}
                  placeholder={t("Select Payer")}
                  value={values.payer_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.payer_id}
                  touched={touched.payer_id}
                  renderOptions={() => (
                    <>
                      <option value="">{t("Select Payer")}</option>
                      {payers.map((category) => (
                        <optgroup
                          key={category.id}
                          label={
                            category?.name_en
                              ? `${category.name_en} (${category.license_id})`
                              : ""
                          }
                        >
                          {category.payers.map((insurance) => (
                            <option key={insurance.id} value={insurance.id}>
                              {insurance.name_en} ({insurance.license_id})
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </>
                  )}
                />
              </div>
            </div>

            <div className="flex items-end mb-0.5 justify-end">
              <AppButton
                label={t("Submit")}
                startIcon={<CloseIcon />}
                type="submit"
                isLoading={submitLoading}
                loadingText={t("Submitting")}
              />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
